import Api, { handleData, handleError } from '../Api';
import request from '../request';
import { ModelOptions } from '../../types';
import { CurrUserDetails } from '../travelFiles/oldModelTypes';
import { clearTravelFilesQueryCache } from '../travelFiles/travelFilesQueryCache';

import { url } from './config';
import { Passenger } from './types';
import { PassengerQueryType } from './helpers';

export type AddPassengerToTravelFile = {
    passenger: Passenger;
    travelFileId: string | undefined;
    classId?: string | number;
    tourId?: string | number;
    currUserDetails: CurrUserDetails;
    crmRefNumber?: string | number | undefined;
};
export type PassengerTravelFileRow = {
    _id: number;
    refNumber: string;
    name: string | null;
    status: string | null;
    startDate: string | null;
    endDate: string | null;
    tourCode: string | null;
    tourName: string | null;
    attribute: string | null;
    agent: string | null;
    currencySymbol: string | null;
    /** Net paid on the whole file, in its currency (receipts − executed repays). */
    paid: number;
    isRemoved: boolean;
    isCancelled: boolean;
};
export type BodyEditPassengerTravelFile = {
    passenger: Partial<Passenger> & { _id: Passenger['_id'] };
    travelFileId: string | undefined;
    currUserDetails: CurrUserDetails;
    oldPassenger: Passenger;
};
export type RemovePassengerFromTravelFile = {
    passengerId: Passenger['_id'];
    travelFileId: string | undefined;
    classId: string | undefined;
    toursId: string[];
    currUserDetails: CurrUserDetails;
    /** Optional free text, stored on travelfilepassengers."cancelReason". */
    cancelReason?: string | null;
};

export type ReinstatePassengerToTravelFile = {
    passengerId: Passenger['_id'];
    travelFileId: string | undefined;
    classId: string | undefined;
    toursId: string[];
    currUserDetails: CurrUserDetails;
};

class ExtendedApi extends Api<Passenger> {
    addPassengerToTravelFile(body: AddPassengerToTravelFile) {
        return request.post(`${url}-add-passenger-to-travel-file`, body).then(handleData).catch(handleError);
    }
    editPassengerTravelFile(body: BodyEditPassengerTravelFile) {
        return request.put(`${url}-edit-passenger-travel-file`, body).then(handleData).catch(handleError);
    }
    removePassengerFromTravelFile(body: RemovePassengerFromTravelFile) {
        return request
            .put(`${url}-remove-passenger-from-travel-file`, body)
            .then(handleData)
            .catch(handleError);
    }
    reinstatePassengerToTravelFile(body: RemovePassengerFromTravelFile) {
        return request
            .put(`${url}-reinstate-passenger-to-travel-file`, body)
            .then(handleData)
            .catch(handleError);
    }
    async query(query: PassengerQueryType, options?: ModelOptions) {
        try {
            const res = await request.get<Passenger[]>(`${url}-query`, {
                params: { ...query, ...options }
            });
            return res.data;
        } catch (err) {
            return handleError(err);
        }
    }

    async queryPaginated(body: {
        query?: Record<string, any>;
        page?: number;
        pageSize?: number;
        sort?: any[];
    }) {
        try {
            const res = await request.post(`${url}-query-paginated`, body);
            return res.data;
        } catch (err) {
            return handleError(err);
        }
    }
    async listTravelFiles(passengerId: string | number) {
        try {
            const res = await request.get(`${url}-travel-files/${passengerId}`);
            return res.data as Array<PassengerTravelFileRow>;
        } catch (err) {
            return handleError(err);
        }
    }
    async listForCopy(travelFileId: string | number) {
        try {
            const res = await request.get(`${url}-list-for-copy/${travelFileId}`);
            return res.data as Array<any>;
        } catch (err) {
            return handleError(err);
        }
    }
    bulkCopyToTravelFile(body: {
        fromTravelFileId: string | number;
        toTravelFileId: string | number;
        passengerIds: Array<string | number>;
        addPassengersPricesAsWell?: boolean;
        currUserDetails?: CurrUserDetails;
    }) {
        // Can change the destination's lead passenger / name and travel agent.
        clearTravelFilesQueryCache();
        return request.post(`${url}-bulk-copy-to-travel-file`, body).then(handleData).catch(handleError);
    }
    moveToTravelFile(body: {
        passengerId: string | number;
        fromTravelFileId: string | number;
        toTravelFileId: string | number;
        currUserDetails?: CurrUserDetails;
    }) {
        clearTravelFilesQueryCache();
        return request.post(`${url}-move-to-travel-file`, body).then(handleData).catch(handleError);
    }
}

const api = new ExtendedApi(url);

export default api;
